import {DataTable} from "../DataTable/DataTable";
import {
  getStringSorter,
  getValueByKey,
  StatisticsGroupWrapper,
  StatisticsType,
  TransportCategory,
  TransportMethod
} from "@ots-frontend/ots-data";
import {useEffect, useMemo, useState} from "react";
import {sortBy} from "lodash";
import {IconChevronUp, IconSelector} from "@tabler/icons-react";
import {StatisticsDataGroup, useStatisticsDataTableColumns} from "./useStatisticsDataTableColumns";
import {DataTableSorterConfig, useDataTableSorter} from "../DataTable/useDataTableSorter";
import {StatisticsChildDataTable} from "./StatisticsChildDataTable";
import {useStatisticsDataTableFilter} from "./useStatisticsDataTableFilter";
import {usePagination} from "../DataTable/usePagination";
import type {DataTableRowExpansionProps} from "mantine-datatable/dist/types";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

export const EMPTY_STATS_ARRAY: StatisticsGroupWrapper[] = []

export type StatisticsDataTableProps =
  Omit<EntityDataTableProps<StatisticsGroupWrapper>, "rowExpansion">
  & {
  childRowExpansion?: DataTableRowExpansionProps<StatisticsGroupWrapper>
  childExpandedIds?: string[]
  rateModel?: string
  category?: TransportCategory
  method?: TransportMethod
  types: StatisticsType[]
  hideExpansion?: boolean
  grouping?: {
    groupRetriever: (stats: StatisticsGroupWrapper) => Record<string, StatisticsGroupWrapper>
    groups: StatisticsDataGroup[]
  },
  showTotals?: boolean
}

export const StatisticsDataTable = (props: StatisticsDataTableProps) => {
  const {
    records: data = EMPTY_STATS_ARRAY,
    rateModel,
    category,
    method,
    types,
    recordsPerPageOptions,
    childRowExpansion,
    childExpandedIds,
    hideExpansion = false,
    showTotals = false,
    grouping,
    ...rest
  } = props;

  const [children, setChildren] = useState<Record<string, StatisticsGroupWrapper[]>>({})
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const {
    filteredRecords,
    filterConfig
  } = useStatisticsDataTableFilter({
    records: data,
    method: method,
    category: category,
  })

  const {
    columns,
    groups,
  } = useStatisticsDataTableColumns({
    filterConfig: filterConfig,
    expandedIds: hideExpansion ? undefined : expandedIds,
    rateModel: rateModel,
    category: category,
    method: method,
    types: types,
    grouping: grouping,
    showTotals: showTotals,
    records: filteredRecords,
  })

  const accessors = useMemo(() => {
    return columns?.map(c => c.accessor) ?? []
  }, [columns])

  const sortConfig = useMemo(() => {
    const config: DataTableSorterConfig<StatisticsGroupWrapper> = {}

    for (const accessor of accessors) {
      const typeIndex = Number(accessor.replace("value", ""))
      if (!isNaN(typeIndex)) {
        const type = types[typeIndex]
        config[accessor] = {
          sortAll: (records, columnAccessor) => {
            return sortBy(records, [function (record) {
              return record.getValue(method, type, rateModel, category)
            }])
          }
        }
      }
    }

    return config
  }, [JSON.stringify(accessors), method, rateModel, category, types])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  useEffect(() => {
    const childrenLookup: Record<string, StatisticsGroupWrapper[]> = {}

    data.forEach((record) => {
      const children = Object.values(record.childMap)
      if (children.length > 0) {
        if (record.id in childrenLookup) {
          childrenLookup[record.id].push(...children)
        } else {
          childrenLookup[record.id] = [...children]
        }
      }
    });

    for (const key in childrenLookup) {
      childrenLookup[key] = childrenLookup[key].sort(getStringSorter("name"))
    }

    setChildren(childrenLookup);
  }, [data]);

  const hasChildren = useMemo(() => {
    return Object.keys(children).length > 0
  }, [children])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
      // groups={groups}
      sortIcons={{
        sorted: <IconChevronUp size={14}/>,
        unsorted: <IconSelector size={14}/>,
      }}
      rowExpansion={!hasChildren ? undefined : {
        allowMultiple: true,
        expanded: {
          recordIds: expandedIds,
          onRecordIdsChange: setExpandedIds
        },
        content: (record) => {
          return (
            <StatisticsChildDataTable
              noHeader
              withColumnBorders
              withBorder={true}
              records={getValueByKey(children, record.record.id) ?? []}
              types={types}
              method={method}
              category={category}
              rateModel={rateModel}
              rowExpansion={childRowExpansion}
              expandedIds={childExpandedIds}
            />
          )
        },
      }}
    />
  );
}
